import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Grid, IconButton, InputAdornment, TextField } from '@mui/material'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import SearchIcon from '@mui/icons-material/Search'
import { updateQuery } from '../state/actions'
import { CustomersBySearchQueryProps, SearchQueryConstants, UseSearchByQuery, apiFilters } from '@cbgms/api/modules/customers'
import { useSafeState } from '@cbgms/base/hooks/use-safe-state'
import { MenuItem } from 'core/page/MenuItem'
import { useNotifier } from '@cbgms/base/notifier'
import { notifier } from '@cbgms/base/notifier/use-notifier'
import store from '../state/store'

export const SearchByQuery: React.FC<CustomersBySearchQueryProps> = ({ setAPISearchQuery, handleReload }) => {
  const [queryInputValue, setqueryInputValue] = useSafeState<string>('')

  const { t } = useTranslation()
  const notifier = useNotifier()

  useEffect(() => {
    // Read current store state immediately on mount (restores search input after navigation)
    const initialQuery = store.getState()?.Query ?? ''
    if (initialQuery) {
      setqueryInputValue(initialQuery)
    }

    const unsubscribe = store.subscribe(() => {
      const state = store.getState()

      const query = state?.Query ?? ''
      setqueryInputValue(query)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  // AFTER:
  const isMounted = React.useRef(false)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return // skip on initial mount — don't clear the store's Query
    }
    if (queryInputValue === '') {
      updateSearchQuery('')
    }
  }, [queryInputValue])

  const updateSearchQuery = React.useCallback(
    async (QueryString: string) => {
      try {
        if (QueryString.length >= SearchQueryConstants.MIN_QUERY_LENGTH) {
          store.dispatch(updateQuery(QueryString))
          const { Query, EcommerceStatus, ModuleID, ActivationStatus } = store.getState()
          const customerSearchQueryInList = await apiFilters({ Query, EcommerceStatus, ModuleID, ActivationStatus })
          setAPISearchQuery(customerSearchQueryInList || '')
        }
        if (QueryString.length <= 0) {
          store.dispatch(updateQuery(QueryString))
          const { Query, EcommerceStatus, ModuleID, ActivationStatus } = store.getState()
          const customerSearchQueryInList = await apiFilters({ Query, EcommerceStatus, ModuleID, ActivationStatus })
          setAPISearchQuery(customerSearchQueryInList || '')
        }
      } catch (e: any) {
        notifier.error(e?.message ?? 'Error updating search query')
      }
    },
    [setAPISearchQuery]
  )

  const queryStringOnChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.target.value.slice(0, 255)
    setqueryInputValue(searchQuery)
    updateQuery(searchQuery)
  }, [])

  const queryStringOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()

      updateSearchQuery(queryInputValue)
    }
  }

  return (
    <Grid item display='flex' alignItems='center' gap={1} data-testid='search-component'>
      <TextField
        size='small'
        sx={{ minWidth: 450 }}
        variant='outlined'
        placeholder={t('grid.search_by_eos_id_cust_name_ship_to_location')}
        type='text'
        onChange={queryStringOnChange}
        onKeyDown={queryStringOnKeyDown}
        value={queryInputValue ?? ''}
        inputProps={{ 'data-testid': 'search-component-input' }}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              {queryInputValue && (
                <IconButton
                  onClick={() => {
                    handleReload && handleReload()
                    setqueryInputValue('')
                    updateSearchQuery('')
                  }}
                  size='large'
                >
                  <HighlightOffIcon />
                </IconButton>
              )}
            </InputAdornment>
          )
        }}
      />
      <Button
        startIcon={<SearchIcon />}
        variant='contained'
        disableElevation
        onClick={() => updateSearchQuery(queryInputValue)}
        disabled={queryInputValue?.length < 3}
        data-testid='search-component-button'
      >
        {t('global.search')}
      </Button>
    </Grid>
  )
}

export const useSearchCustomers = ({ handleReload }: { handleReload: () => void }): UseSearchByQuery => {
  const [apiSearchQuery, setAPISearchQuery] = useSafeState<string>('')
  async function applyFiltersToQuery() {
    try {
      const { Query, EcommerceStatus, ModuleID, ActivationStatus } = store.getState()
      const query = await apiFilters({
        Query,
        EcommerceStatus,
        ModuleID,
        ActivationStatus
      })
      if (query) {
        setAPISearchQuery(query)
      } else {
        setAPISearchQuery('')
      }
    } catch (e: any) {
      notifier.error(e?.message ?? 'Error updating list by dropdown filters')
    }
  }
  // Restore search state when navigating back from ConceptManagement
  useEffect(() => {
    applyFiltersToQuery()
  }, [])

  async function updateListBYDropDownFilters() {
    try {
      await applyFiltersToQuery()
    } catch (e: any) {
      notifier.error(e?.message ?? 'Error updating list by dropdown filters')
    }
  }

  return {
    getSearchComponent: () => (
      <MenuItem>
        <SearchByQuery applyFiltersToQuery={applyFiltersToQuery} handleReload={handleReload} setAPISearchQuery={setAPISearchQuery} />
      </MenuItem>
    ),
    apiSearchQuery,
    updateListBYDropDownFilters
  }
}
